import { useEffect, useState } from "react";
import { getSubmission, type SubmissionDetail } from "../actions";

/**
 * What somebody filled in on a form on the website.
 *
 * The submission was always kept — whole, immutably, from the moment it arrived —
 * and never shown. The person's card said "בקשת שירות" and stopped there, which
 * read as a CRM that could not take the forms at all.
 *
 * Deliberately dumb: it shows every field the server hands back, in the order it
 * hands them back, and holds no opinion about which ones matter. The ordering and
 * the Hebrew labels are the server's job, and a field nobody has declared yet is
 * still shown — under its own key, which is ugly and is much better than losing
 * it. That is what lets a new form on the website work before anybody teaches the
 * CRM about it.
 */
export function SubmissionSheet({
  submissionId,
  onClose,
}: {
  submissionId: string;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<SubmissionDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    getSubmission(submissionId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        const message = (e as { message?: string })?.message;
        setErr(
          message && /[֐-׿]/.test(message) ? message : "לא הצלחנו לפתוח את הפנייה. נסו שוב.",
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  const when = detail?.occurredAt ?? detail?.receivedAt ?? null;

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label="פנייה מהאתר">
        <div className="sheet-grip" />

        <div className="sheet-head">
          <h2>{detail?.title ?? "פנייה מהאתר"}</h2>
        </div>
        {when && (
          <p className="faint" style={{ marginTop: 0 }}>
            {new Date(when).toLocaleDateString("he-IL", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
            {detail?.page ? ` · ${detail.page}` : ""}
          </p>
        )}

        <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {loading && <div className="skeleton" style={{ height: 120 }} />}
          {err && <div className="notice error">{err}</div>}

          {!loading && !err && detail && detail.fields.length === 0 && (
            <p className="faint">
              הטופס נשלח בלי תוכן נוסף. היו בו רק פרטי קשר, והם כבר בכרטיס.
            </p>
          )}

          {!loading &&
            !err &&
            detail?.fields.map((f) => (
              <div key={f.key} className="form-field">
                <span className="form-label">{f.label}</span>
                {/* Pre-wrap, because a free-text message arrives with the line
                    breaks the person typed and flattening them loses the sense. */}
                <p style={{ margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{f.value}</p>
              </div>
            ))}
        </div>

        <div className="btn-row">
          <button className="btn" onClick={onClose}>
            סגירה
          </button>
        </div>
      </div>
    </>
  );
}
